import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { orderBy } from 'lodash-es';
import Axios from 'axios';
import * as Sentry from '@sentry/vue';
import { useGlobalStore } from '@/store/store.ts';
import { htmlToTextPreserveBreaks } from '@/utils/html.ts';
import { useUserStore } from '@/store/modules/user/store.js';
import { useAccountStore } from '@/store/modules/account/store.js';
import { useContextStore } from '@/store/modules/context/store.ts';
import ConversationAPI from '@/api/conversations/index.ts';
import templates from '@/api/templates.js';
import {
    ApiConversation,
    ApiContactTarget,
    ApiContextLead,
    ApiConversationUser,
    AppointmentStatusChangedPayload,
    ConversationNotFoundResponse,
    ConversationKind,
    MessageItem,
    NewSmsMessageBroadcastPayload,
    PaginatedMessageListWithConversation,
    AttachmentMessagePayload,
    SendDisabledReason,
    SendMessageResponse,
    TextMessagePayload,
} from '@/api/conversations/types/index.ts';
import type { Account as AccountType } from '@/entities/types/LeadType.ts';
import { clearBlobUrlCache } from '@/hooks/useAttachmentDownload.ts';
import CommunicationType from '@/entities/CommunicationType.js';
import AuthUser from '@/entities/AuthUser.js';
import Conversation from '@/entities/Conversation.ts';
import ConversationLead from '@/entities/ConversationLead.ts';
import ConversationLeadPhone from '@/entities/ConversationLeadPhone.ts';
import ConversationContactPhone from '@/entities/ConversationContactPhone.ts';
import ConversationSmsProvider from '@/entities/ConversationSmsProvider.ts';
import ConversationUser from '@/entities/ConversationUser.ts';
import { AppointmentStatus, Message } from '@/entities/types/ConversationType.ts';
import { FetchStatus } from '@/store/modules/niotext/FetchStatus.ts';
import ActivixDate from '@/value-objects/ActivixDate.js';

export interface FetchConversationsParams {
    accountId: number;
    search?: string;
    intelligentFilter?: boolean;
    archived?: boolean;
}

type SendTextMessageStorePayload = Pick<TextMessagePayload, 'body' | 'ai_interaction_id'>;
type SendAttachmentMessageStorePayload = Pick<
    AttachmentMessagePayload,
    'key' | 'name' | 'content_type'
>;
type SendMessageStorePayload = SendTextMessageStorePayload | SendAttachmentMessageStorePayload;
type ReadSyncState = 'idle' | 'pending' | 'syncing';
type SendAvailabilityPayload = {
    canSend?: boolean;
    sendDisabledReason?: SendDisabledReason;
};
type ConversationAvailabilityPayload = SendAvailabilityPayload & { readOnly: boolean };
type ConversationPageWithAvailability = {
    response: PaginatedMessageListWithConversation;
    exactCtaAvailability: ConversationAvailabilityPayload | null;
    capabilityRefreshId: number | null;
};

export interface SmsConversationSendTarget {
    lead: ConversationLead;
    leadPhoneId: number | null;
    contactPhoneId: number | null;
}

// Caps one fetch round so a cursor that keeps advancing without yielding rows cannot loop forever.
const MAX_EMPTY_PAGE_SKIPS = 5;

/**
 * Shares one in-flight round between concurrent callers instead of letting them race each other to
 * the shared cursor. A different key starts a new round; an older completion only releases its
 * own slot, even if the same key has since been reused.
 */
const createFetchCoalescer = (currentKey: () => number | string) => {
    let inFlight: { key: number | string; promise: Promise<FetchStatus> } | null = null;

    return (run: () => Promise<FetchStatus>): Promise<FetchStatus> => {
        const key = currentKey();

        if (inFlight?.key === key) {
            return inFlight.promise;
        }

        const release = (): void => {
            if (inFlight?.promise === promise) {
                inFlight = null;
            }
        };

        const promise = run().finally(release);

        inFlight = { key, promise };

        return promise;
    };
};

export const useNiotextStore = defineStore('niotext', () => {
    // --- State ---
    const api = new ConversationAPI();
    const conversations = ref<Conversation[]>([]);
    const conversationsFetchId = ref(Date.now());
    const conversationsNextPageCursor = ref<string | null>(null);

    const selectedConversationId = ref<number | null>(null);
    const selectedConversationKind = ref<ConversationKind | null>(null);

    const selectedConversationMessages = ref<Message[]>([]);
    const messagesFetchId = ref(Date.now());
    const messagesNextPageCursor = ref<string | null>(null);
    const deferredMessagesNextPageCursor = ref<string | null>(null);
    const selectedConversationLastMessageReadIdAtLoad = ref<number | null>(null);
    const selectedConversationReadOnly = ref<boolean>(false);
    const selectedConversationCanSend = ref<boolean>(true);
    const selectedConversationSendDisabledReason = ref<SendDisabledReason>(null);
    const selectedConversationCtaAvailability = ref<ConversationAvailabilityPayload | null>(null);
    const selectedConversationContextLead = ref<ConversationLead | null>(null);
    const selectedConversationContextLeadPhone = ref<ConversationLeadPhone | null>(null);
    const selectedConversationSendTarget = ref<SmsConversationSendTarget | null>(null);
    const selectedConversationSourceCommunicationId = ref<number | null>(null);
    const conversationSelectionGeneration = ref(0);
    let conversationLookupGeneration = 0;
    let latestCtaCapabilityRefreshId = 0;
    const readSyncState = ref<ReadSyncState>('idle');
    const retryingMessageIds = ref<Set<number>>(new Set());

    const nextDraftId = ref<number>(-1);

    const conversationKey = (kind: ConversationKind, id: number): string => `${kind}:${id}`;

    const selectedConversationIdentityKey = computed<string | null>(() => {
        if (!selectedConversationId.value || !selectedConversationKind.value) {
            return null;
        }

        return conversationKey(selectedConversationKind.value, selectedConversationId.value);
    });

    const selectedConversationMatches = (kind: ConversationKind, id: number): boolean => {
        return selectedConversationIdentityKey.value === conversationKey(kind, id);
    };

    const getConversationByIdentityKey = (identityKey: string): Conversation | null => {
        return (
            conversations.value.find((conversation) => conversation.identityKey === identityKey) ??
            null
        );
    };

    const getConversationById = (
        id: number,
        kind: ConversationKind = ConversationKind.NATIVE
    ): Conversation | null => {
        return getConversationByIdentityKey(conversationKey(kind, id));
    };

    const resolveCurrentUserIdForAccount = (accountId?: number | null): number | null => {
        const parentAuthUser = useUserStore().parentAuthUser as AuthUser | null;

        return parentAuthUser?.selfOrChildUserForAccount(accountId)?.id ?? null;
    };

    // --- Computed getters ---
    const selectedConversation = computed<Conversation | null>(() => {
        if (!selectedConversationIdentityKey.value) {
            return null;
        }

        return getConversationByIdentityKey(selectedConversationIdentityKey.value);
    });

    const selectedConversationDisplayLead = computed<ConversationLead | null>(() => {
        return selectedConversationContextLead.value ?? selectedConversation.value?.lead ?? null;
    });

    const selectedConversationDisplayPhone = computed<
        ConversationLeadPhone | ConversationContactPhone | null
    >(() => {
        return (
            selectedConversationContextLeadPhone.value ??
            selectedConversation.value?.leadPhone ??
            selectedConversation.value?.contactPhone ??
            null
        );
    });

    const currentLead = computed<ConversationLead | null>(() => {
        return (
            selectedConversationSendTarget.value?.lead ??
            selectedConversationDisplayLead.value ??
            selectedConversation.value?.lead ??
            null
        );
    });

    const currentLeadId = computed(() => currentLead.value?.id ?? null);

    const conversationCount = computed(() => {
        return conversations.value.length;
    });

    const hasFetchedAllConversations = computed(() => {
        return conversationCount.value > 0 && !conversationsNextPageCursor.value;
    });

    const hasFetchedAllMessages = computed(() => {
        return !!selectedConversationMessages.value.length && !messagesNextPageCursor.value;
    });

    // --- Actions ---
    const appendUniqueConversations = (newConversations: Conversation[]): number => {
        const existingIdentityKeys = new Set(
            conversations.value.map((conversation) => conversation.identityKey)
        );
        const uniqueConversations = newConversations.filter(
            (conversation) => !existingIdentityKeys.has(conversation.identityKey)
        );

        conversations.value.push(...uniqueConversations);

        return uniqueConversations.length;
    };

    const coalesceConversationsFetch = createFetchCoalescer(() => conversationsFetchId.value);

    const runConversationsFetch = async (
        payload: FetchConversationsParams
    ): Promise<FetchStatus> => {
        const fetchIdAtRequestStart = conversationsFetchId.value;

        try {
            // A successful page can append nothing: every row was already loaded, or lenient
            // validation dropped them all. Reporting SUCCESS there re-arms infinite loading on a
            // sentinel that has not moved, so skip ahead while the cursor advances instead.
            for (let attempt = 0; attempt < MAX_EMPTY_PAGE_SKIPS; attempt++) {
                const requestedCursor = conversationsNextPageCursor.value;

                // eslint-disable-next-line no-await-in-loop -- Sequential page fetching is intentional
                const response = await api.fetchConversations({
                    search: '',
                    intelligentFilter: true,
                    ...payload,
                    perPage: 15,
                    page: requestedCursor,
                });

                if (conversationsFetchId.value !== fetchIdAtRequestStart) {
                    // The list was reset while this page was in flight. Keeping its cursor would
                    // page the new filters through the previous result set.
                    return FetchStatus.CANCELED;
                }

                conversationsNextPageCursor.value = response.meta.next_cursor;

                const appendedCount = appendUniqueConversations(
                    response.data.map((item: ApiConversation) =>
                        Conversation.fromApi(
                            item,
                            {},
                            resolveCurrentUserIdForAccount(item.lead.account_id)
                        )
                    )
                );

                if (appendedCount > 0) {
                    return FetchStatus.SUCCESS;
                }

                if (!response.meta.next_cursor || response.meta.next_cursor === requestedCursor) {
                    // No further page, or the cursor is stuck on the one we just requested.
                    return FetchStatus.NO_MORE_RESULTS;
                }
            }

            // The skip budget is spent while the cursor is still advancing, so the remaining pages
            // are not exhausted. Stop this round without letting the caller disconnect the loader.
            return FetchStatus.PAUSED;
        } catch (error: any) {
            if (Axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
                // Keep infinite loading active when a request is superseded by a new one.
                return FetchStatus.CANCELED;
            }

            throw error;
        }
    };

    const fetchConversations = (payload: FetchConversationsParams): Promise<FetchStatus> => {
        return coalesceConversationsFetch(() => runConversationsFetch(payload));
    };

    const findConversationIndex = (
        conversationId: number,
        kind: ConversationKind = ConversationKind.NATIVE,
        previousConversationId: number | null = null
    ): number => {
        return conversations.value.findIndex((currentConversation) => {
            return (
                currentConversation.identityKey === conversationKey(kind, conversationId) ||
                (previousConversationId !== null &&
                    // During draft promotion, previousConversationId is the local draft id.
                    // Treat it as an alternate identity for the resolved conversation.
                    currentConversation.identityKey ===
                        conversationKey(ConversationKind.NATIVE, previousConversationId))
            );
        });
    };

    const prependConversation = (conversation: Conversation): void => {
        const existingIndex = findConversationIndex(conversation.id, conversation.kind);

        if (existingIndex !== -1) {
            conversations.value.splice(existingIndex, 1);
        }

        conversations.value.unshift(conversation);
    };

    const addConversation = (conversation: Conversation): void => {
        prependConversation(conversation);
    };

    const addAndSelectConversation = (conversation: Conversation): void => {
        addConversation(conversation);
        setSelectedConversationId(conversation.id, conversation.kind);
    };

    const buildDefaultSendTarget = (
        conversation: Conversation
    ): SmsConversationSendTarget | null => {
        if (conversation.isLegacy) {
            return null;
        }

        if (conversation.contactPhone) {
            return {
                lead: conversation.lead,
                leadPhoneId: null,
                contactPhoneId: conversation.contactPhone.id,
            };
        }

        if (!conversation.leadPhone) {
            return null;
        }

        return {
            lead: conversation.lead,
            leadPhoneId: conversation.leadPhone.id,
            contactPhoneId: null,
        };
    };

    const buildContactSendTarget = (
        contactTarget: ApiContactTarget,
        lead: ConversationLead
    ): SmsConversationSendTarget | null => {
        if (contactTarget.lead_id !== lead.id) {
            return null;
        }

        return {
            lead,
            leadPhoneId: contactTarget.lead_phone_id,
            contactPhoneId: contactTarget.contact_phone_id,
        };
    };

    const buildContextLead = (
        contextLead: ApiContextLead | null | undefined,
        fallbackLead: ConversationLead
    ): ConversationLead => {
        if (!contextLead) {
            return fallbackLead;
        }

        const fallbackMatchesContext = fallbackLead.id === contextLead.id;

        return new ConversationLead({
            id: contextLead.id,
            accountId: contextLead.account_id,
            customerId: contextLead.customer_id,
            fullName: `${contextLead.first_name} ${contextLead.last_name}`.trim(),
            divisionId: contextLead.division_id,
            locale: contextLead.locale,
            cellPhones: fallbackMatchesContext ? fallbackLead.cellPhones : [],
            isUnsubscribedToSms: fallbackMatchesContext ? fallbackLead.isUnsubscribedToSms : false,
            isCaslExpired: fallbackMatchesContext ? fallbackLead.isCaslExpired : false,
        });
    };

    const resetSelectedConversationContext = (): void => {
        selectedConversationContextLead.value = null;
        selectedConversationContextLeadPhone.value = null;
        selectedConversationSendTarget.value = null;
        selectedConversationSourceCommunicationId.value = null;
        selectedConversationCtaAvailability.value = null;
    };

    const advanceConversationSelectionGeneration = (): number => {
        conversationSelectionGeneration.value++;
        conversationLookupGeneration++;

        return conversationSelectionGeneration.value;
    };

    const applySelectedConversationContext = (
        conversation: Conversation,
        options: {
            contextLead?: ConversationLead | null;
            contextLeadPhone?: ConversationLeadPhone | null;
            sendTarget?: SmsConversationSendTarget | null;
            sourceCommunicationId?: number | null;
        } = {}
    ): void => {
        selectedConversationContextLead.value = options.contextLead ?? null;
        selectedConversationContextLeadPhone.value = options.contextLeadPhone ?? null;
        if (options.sendTarget === undefined) {
            selectedConversationSendTarget.value = buildDefaultSendTarget(conversation);
        } else {
            // Explicit null clears the send target, so nullish coalescing would change this behavior.
            selectedConversationSendTarget.value = options.sendTarget;
        }
        selectedConversationSourceCommunicationId.value = options.sourceCommunicationId ?? null;
    };

    const buildDraftConversation = (
        lead: ConversationLead,
        phone: ConversationLeadPhone,
        smsProvider: ConversationSmsProvider,
        contactTarget?: ApiContactTarget
    ): Conversation => {
        return new Conversation({
            id: nextDraftId.value--,
            lead,
            leadPhone: contactTarget ? null : phone,
            contactPhone: contactTarget
                ? new ConversationContactPhone({
                      id: contactTarget.contact_phone_id,
                      phone: contactTarget.destination,
                      type: phone.type,
                  })
                : null,
            smsProvider,
            time: new Date().toISOString(),
        });
    };

    const addDraftConversation = (
        lead: ConversationLead,
        phone: ConversationLeadPhone,
        smsProvider: ConversationSmsProvider,
        contactTarget?: ApiContactTarget
    ): void => {
        const conversation = buildDraftConversation(lead, phone, smsProvider, contactTarget);
        addAndSelectConversation(conversation);

        if (contactTarget) {
            applySelectedConversationContext(conversation, {
                contextLead: lead,
                contextLeadPhone: phone,
                sendTarget: buildContactSendTarget(contactTarget, lead),
            });
        }
    };

    const addOrderedMessagesFromApi = (items: MessageItem[]): number => {
        const formattedMessages = items.map((item: MessageItem) => formatMessage(item));
        const orderedMessages = orderBy(formattedMessages, ['createdAt'], ['desc']);
        const countBeforeAdding = selectedConversationMessages.value.length;

        for (const message of orderedMessages) {
            addMessage(message);
        }

        // What was actually added, not what the API returned: addMessage() drops known messages.
        return selectedConversationMessages.value.length - countBeforeAdding;
    };

    const isReadSyncPending = (): boolean => readSyncState.value === 'pending';
    const isReadSyncInFlight = (): boolean => readSyncState.value === 'syncing';

    const setReadSyncIdle = (): void => {
        readSyncState.value = 'idle';
    };

    const setReadSyncPending = (): void => {
        readSyncState.value = 'pending';
    };

    const setReadSyncInFlight = (): void => {
        readSyncState.value = 'syncing';
    };

    const applyConversationAvailability = (payload: ConversationAvailabilityPayload): void => {
        const ctaAvailability = selectedConversationCtaAvailability.value;

        selectedConversationReadOnly.value = payload.readOnly || !!ctaAvailability?.readOnly;
        selectedConversationCanSend.value =
            !selectedConversationReadOnly.value &&
            (payload.canSend ?? true) &&
            (ctaAvailability?.canSend ?? true);
        selectedConversationSendDisabledReason.value =
            ctaAvailability?.sendDisabledReason ?? payload.sendDisabledReason ?? null;
    };

    const fetchThreadPageWithExactCta = async (
        conversationId: number,
        page: string | null,
        ctaTarget: SmsConversationSendTarget | null,
        fetchGenericPage: () => Promise<PaginatedMessageListWithConversation>
    ): Promise<ConversationPageWithAvailability> => {
        if (ctaTarget?.leadPhoneId) {
            const capabilityRefreshId = ++latestCtaCapabilityRefreshId;

            try {
                const exact = await api.findConversationByLeadPhone({
                    leadId: ctaTarget.lead.id,
                    leadPhoneId: ctaTarget.leadPhoneId,
                    perPage: 25,
                    ...(page ? { page } : {}),
                });

                if (
                    exact &&
                    !isConversationNotFoundResponse(exact) &&
                    exact.conversation.data.id === conversationId
                ) {
                    return {
                        response: exact,
                        capabilityRefreshId,
                        exactCtaAvailability: {
                            readOnly: exact.readOnly,
                            canSend: exact.canSend,
                            sendDisabledReason: exact.sendDisabledReason,
                        },
                    };
                }
            } catch (error: any) {
                if (![403, 404, 422].includes(error?.response?.status)) {
                    throw error;
                }
            }

            return {
                response: await fetchGenericPage(),
                capabilityRefreshId,
                exactCtaAvailability: {
                    readOnly: false,
                    canSend: false,
                    sendDisabledReason: 'contact_target_unavailable',
                },
            };
        }

        return {
            response: await fetchGenericPage(),
            exactCtaAvailability: null,
            capabilityRefreshId: null,
        };
    };

    const applyConversationMessagesResponse = (
        response: PaginatedMessageListWithConversation,
        fetchIdAtRequestStart: number,
        conversationId: number,
        conversationKind: ConversationKind,
        isFirstPage: boolean,
        exactCtaAvailability: ConversationAvailabilityPayload | null,
        capabilityRefreshId: number | null
    ): FetchStatus => {
        if (
            !selectedConversationMatches(conversationKind, conversationId) ||
            messagesFetchId.value !== fetchIdAtRequestStart
        ) {
            // The selected conversation changed while this request was in flight.
            // Ignore stale responses so they do not pollute a different
            // conversation or a newer fetch cycle.
            return FetchStatus.CANCELED;
        }

        messagesNextPageCursor.value = response.meta.next_cursor;

        if (isFirstPage) {
            selectedConversationLastMessageReadIdAtLoad.value = response.lastMessageReadId;
        }

        if (capabilityRefreshId === null || capabilityRefreshId === latestCtaCapabilityRefreshId) {
            if (exactCtaAvailability) {
                selectedConversationCtaAvailability.value = exactCtaAvailability;
            }

            applyConversationAvailability(response);
        }

        if (response.conversation?.data?.conversationUsers) {
            updateConversationUsers(
                response.conversation.data.id,
                response.conversation.data.conversationUsers,
                response.conversation.data.kind
            );
        }

        const addedMessagesCount = addOrderedMessagesFromApi(response.data);

        if (addedMessagesCount > 0) {
            return FetchStatus.SUCCESS;
        }

        return FetchStatus.NO_MORE_RESULTS;
    };

    const coalesceMessagesFetch = createFetchCoalescer(
        () => `${messagesFetchId.value}:${selectedConversationIdentityKey.value}`
    );

    const runConversationMessagesFetch = async (conversationId: number): Promise<FetchStatus> => {
        const conversationKind = selectedConversationKind.value ?? ConversationKind.NATIVE;
        const sourceCommunicationId = selectedConversationSourceCommunicationId.value;
        const fetchIdAtRequestStart = messagesFetchId.value;
        const ctaTarget =
            conversationKind === ConversationKind.NATIVE &&
            !sourceCommunicationId &&
            selectedConversationCtaAvailability.value
                ? selectedConversationSendTarget.value
                : null;
        const fetchPage = (page: string | null): Promise<ConversationPageWithAvailability> => {
            if (sourceCommunicationId) {
                return api
                    .fetchConversationByCommunicationId(sourceCommunicationId, {
                        perPage: 25,
                        page,
                    })
                    .then((response) => ({
                        response,
                        exactCtaAvailability: null,
                        capabilityRefreshId: null,
                    }));
            }

            return fetchThreadPageWithExactCta(conversationId, page, ctaTarget, () =>
                api.fetchConversationThread(conversationId, {
                    perPage: 25,
                    page,
                    kind: conversationKind,
                })
            );
        };

        try {
            // Same hazard as the conversation list: a thread page can dedupe down to nothing while
            // still handing back an advancing cursor, and treating that as exhausted would leave
            // the rest of the history unreachable.
            for (let attempt = 0; attempt < MAX_EMPTY_PAGE_SKIPS; attempt++) {
                const requestedCursor = messagesNextPageCursor.value;

                // eslint-disable-next-line no-await-in-loop -- Sequential page fetching is intentional
                const { response, exactCtaAvailability, capabilityRefreshId } = await fetchPage(
                    requestedCursor
                );

                const status = applyConversationMessagesResponse(
                    response,
                    fetchIdAtRequestStart,
                    conversationId,
                    conversationKind,
                    !requestedCursor,
                    exactCtaAvailability,
                    capabilityRefreshId
                );

                // NO_MORE_RESULTS here only means this page added nothing; whether paging is over
                // is decided by the cursor below.
                if (status !== FetchStatus.NO_MORE_RESULTS) {
                    return status;
                }

                if (!response.meta.next_cursor || response.meta.next_cursor === requestedCursor) {
                    return FetchStatus.NO_MORE_RESULTS;
                }
            }

            return FetchStatus.PAUSED;
        } catch (error: any) {
            if (Axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
                // Keep infinite loading active when a request is superseded by a new one.
                return FetchStatus.CANCELED;
            }

            throw error;
        }
    };

    const fetchConversationMessages = (): Promise<FetchStatus> => {
        const conversationId = selectedConversationId.value;

        if (!conversationId || conversationId < 0) {
            return Promise.resolve(FetchStatus.NO_CONVERSATION_SELECTED);
        }

        return coalesceMessagesFetch(() => runConversationMessagesFetch(conversationId));
    };

    const parseTemplate = async (
        templateId: number,
        locale: string,
        leadId: number
    ): Promise<string> => {
        const parsedTemplate = await templates.parse(templateId, locale, leadId);

        return htmlToTextPreserveBreaks(parsedTemplate.parsedBody || '');
    };

    const sendMessage = async (payload: SendMessageStorePayload) => {
        const conversation = selectedConversation.value;
        const sendTarget = selectedConversationSendTarget.value;
        const selectionGenerationAtRequestStart = conversationSelectionGeneration.value;
        const conversationsFetchIdAtRequestStart = conversationsFetchId.value;

        if (
            selectedConversationReadOnly.value ||
            !selectedConversationCanSend.value ||
            conversation?.isLegacy ||
            !conversation?.smsProvider ||
            !sendTarget
        ) {
            throw new Error('SMS send target unavailable');
        }

        let recipientTarget: { contactPhoneId: number } | { leadPhoneId: number } | null = null;

        if (sendTarget.contactPhoneId) {
            recipientTarget = { contactPhoneId: sendTarget.contactPhoneId };
        } else if (sendTarget.leadPhoneId) {
            recipientTarget = { leadPhoneId: sendTarget.leadPhoneId };
        }

        if (!recipientTarget) {
            throw new Error('SMS send target unavailable');
        }

        const messageTarget =
            conversation.id > 0
                ? { conversationId: conversation.id }
                : { smsProviderId: conversation.smsProvider.id };

        const response = await api.sendMessage({
            ...payload,
            ...messageTarget,
            ...recipientTarget,
            leadId: sendTarget.lead.id,
        });

        // A list reset can happen while the send is pending. Its response belongs to the old
        // list and must not repopulate the new one. A concurrent send may replace the original
        // conversation object without resetting the list, so object identity is not a fence.
        if (conversationsFetchId.value === conversationsFetchIdAtRequestStart) {
            const message = formatMessage(response.data);
            const selectionIsCurrent = promoteResolvedConversation(
                response,
                conversation,
                selectionGenerationAtRequestStart
            );

            if (selectionIsCurrent) {
                addMessage(message, true);
            } else {
                updateConversationLatestMessage(message);
            }
        }

        if (sendTarget.contactPhoneId) {
            try {
                // The first contact-aware send protects this lead's phone history. Refresh the
                // captured lead even if the drawer selection or conversation list has changed.
                await useGlobalStore().reloadLead(sendTarget.lead.id);
            } catch (error) {
                // Sending already succeeded; a refresh failure must not prompt a duplicate send.
                Sentry.captureException(error);
            }
        }
    };

    const addMessage = (message: Message, append: boolean = false) => {
        const messageAlreadyExist = selectedConversationMessages.value.some(
            (item) => item.id === message.id
        );

        if (messageAlreadyExist) {
            return;
        }

        if (append) {
            // Insert in sorted position by ID to maintain chronological order
            // even when concurrent broadcast API responses resolve out of order.
            const insertIndex = selectedConversationMessages.value.findIndex(
                (item) => item.id > message.id
            );

            if (insertIndex === -1) {
                selectedConversationMessages.value.push(message);
            } else {
                selectedConversationMessages.value.splice(insertIndex, 0, message);
            }

            updateConversationLatestMessage(message);
        } else {
            selectedConversationMessages.value.unshift(message);
        }
    };

    const updateMessageStatus = (
        messageId: number,
        status: string | null,
        errorCode: string | null
    ): void => {
        const message = selectedConversationMessages.value.find(
            (message) => message.id === messageId
        );

        if (!message) {
            return;
        }

        message.status = status;
        message.errorCode = errorCode;
    };

    const updateConversationLatestMessage = (message: Message): void => {
        const messageConversation = conversations.value.find(
            (conversation) =>
                conversation.identityKey ===
                conversationKey(message.conversationKind, message.conversationId)
        );

        if (!messageConversation) {
            return;
        }

        if (
            messageConversation.latestMessageId !== null &&
            message.id < messageConversation.latestMessageId
        ) {
            return;
        }

        const shouldPreserveExistingPreview =
            messageConversation.latestMessageId === message.id &&
            !message.text &&
            !!messageConversation.lastMessage;

        messageConversation.latestMessageId = message.id;
        messageConversation.lastMessage = shouldPreserveExistingPreview
            ? messageConversation.lastMessage
            : message.text || '';
        messageConversation.time = message.createdAt || '';
    };

    const markConversationAsUnread = (conversation: Conversation): void => {
        if (!conversation.currentUserId) {
            return;
        }

        const currentConversationUser = conversation.conversationUsers.find(
            (conversationUser) => conversationUser.userId === conversation.currentUserId
        );

        if (!currentConversationUser) {
            return;
        }

        currentConversationUser.allMessagesReadAt = null;
    };

    const shouldMarkConversationAsUnread = (
        conversation: Conversation,
        payload: NewSmsMessageBroadcastPayload
    ): boolean => {
        if (selectedConversationIdentityKey.value === conversation.identityKey) {
            return false;
        }

        if (!conversation.currentUserId) {
            return false;
        }

        return payload.userId !== conversation.currentUserId;
    };

    const applyNewSmsMessagePatch = (
        conversation: Conversation,
        payload: NewSmsMessageBroadcastPayload
    ): void => {
        if (
            conversation.latestMessageId !== null &&
            conversation.latestMessageId >= payload.messageId
        ) {
            return;
        }

        conversation.latestMessageId = payload.messageId;
        conversation.lastMessage = payload.preview || '';
        conversation.time = payload.createdAt;

        if (shouldMarkConversationAsUnread(conversation, payload)) {
            markConversationAsUnread(conversation);
        }

        prependConversation(conversation);
    };

    const handleNewSmsMessageBroadcast = async (
        payload: NewSmsMessageBroadcastPayload
    ): Promise<void> => {
        const conversation = getConversationById(payload.conversationId);

        if (!conversation) {
            return;
        }

        const isStaleBroadcast =
            conversation.latestMessageId !== null &&
            payload.messageId < conversation.latestMessageId;

        if (isStaleBroadcast) {
            return;
        }

        const needsReadSync = shouldSyncSelectedConversationReadState(conversation, payload);

        if (needsReadSync) {
            setReadSyncPending();
        }

        applyNewSmsMessagePatch(conversation, payload);
        await ensureSelectedConversationMessageLoaded(payload.conversationId, payload.messageId);

        if (needsReadSync && isReadSyncPending()) {
            markConversationAsUnread(conversation);
        }
    };

    const shouldSyncSelectedConversationReadState = (
        conversation: Conversation,
        payload: NewSmsMessageBroadcastPayload
    ): boolean => {
        if (selectedConversationIdentityKey.value !== conversation.identityKey) {
            return false;
        }

        if (selectedConversationReadOnly.value) {
            return false;
        }

        if (!conversation.currentUserId) {
            return false;
        }

        // Inbound payloads never have userId and should be claimed as read
        // when the conversation is currently open.
        if (!payload.userId) {
            return true;
        }

        return payload.userId !== conversation.currentUserId;
    };

    const syncSelectedConversationReadState = async (): Promise<void> => {
        const conversation = selectedConversation.value;
        const selectionGeneration = conversationSelectionGeneration.value;

        if (!conversation || selectedConversationReadOnly.value) {
            return;
        }

        if (!isReadSyncPending() || isReadSyncInFlight()) {
            return;
        }

        const currentConversationUser = conversation.conversationUsers.find(
            (conversationUser) => conversationUser.userId === conversation.currentUserId
        );

        if (currentConversationUser) {
            currentConversationUser.allMessagesReadAt = new Date().toISOString();
        }

        setReadSyncInFlight();

        try {
            const ctaTarget = selectedConversationCtaAvailability.value
                ? selectedConversationSendTarget.value
                : null;
            const { response, exactCtaAvailability, capabilityRefreshId } =
                await fetchThreadPageWithExactCta(conversation.id, null, ctaTarget, () =>
                    api.fetchConversationThread(
                        conversation.id,
                        { perPage: 25 },
                        { once: 'niotext-sync-selected-conversation-read-state' }
                    )
                );

            if (
                conversationSelectionGeneration.value !== selectionGeneration ||
                selectedConversationIdentityKey.value !== conversation.identityKey
            ) {
                return;
            }

            if (
                capabilityRefreshId === null ||
                capabilityRefreshId === latestCtaCapabilityRefreshId
            ) {
                if (exactCtaAvailability) {
                    selectedConversationCtaAvailability.value = exactCtaAvailability;
                }

                applyConversationAvailability(response);
            }

            if (response?.conversation?.data?.conversationUsers) {
                updateConversationUsers(
                    response.conversation.data.id,
                    response.conversation.data.conversationUsers,
                    response.conversation.data.kind
                );
            }

            setReadSyncIdle();
            await refreshSmsBadgeCount();
        } catch {
            // Best effort sync: unread counters will still self-heal on next inbox open.
        } finally {
            if (
                conversationSelectionGeneration.value === selectionGeneration &&
                isReadSyncInFlight()
            ) {
                setReadSyncPending();
            }
        }
    };

    const refreshSmsBadgeCount = async (): Promise<void> => {
        const contextAccount = useAccountStore().contextAccount as AccountType | null;
        const accountId = contextAccount?.id;

        if (!accountId) {
            return;
        }

        await useContextStore().refreshSmsBadgeCountAction(accountId);
    };

    const handleAppointmentStatusChanged = (payload: AppointmentStatusChangedPayload): void => {
        const messages = selectedConversationMessages.value;
        const targetMessages = messages.filter(
            (message) => !message.isIncoming && message.appointmentId === payload.appointmentId
        );

        for (const targetMessage of targetMessages) {
            if (!targetMessage.appointment) {
                continue;
            }

            targetMessage.appointment.confirmed = payload.confirmed;
            targetMessage.appointment.canceled = payload.canceled;
        }
    };

    /**
     * Reset the fetch parameters for the conversations list.
     */
    const bumpConversationsFetchId = (): void => {
        conversationsFetchId.value++;
    };

    const resetConversationsFetch = (): void => {
        advanceConversationSelectionGeneration();
        conversations.value = [];
        selectedConversationId.value = null;
        selectedConversationKind.value = null;
        conversationsNextPageCursor.value = null;
        resetSelectedConversationContext();
        applyConversationAvailability({ readOnly: false });
        bumpConversationsFetchId();
    };

    /**
     * Reset the fetch parameters for the messages list.
     */
    const resetMessagesFetch = (): void => {
        selectedConversationMessages.value = [];
        messagesNextPageCursor.value = null;
        deferredMessagesNextPageCursor.value = null;
        selectedConversationLastMessageReadIdAtLoad.value = null;
        applyConversationAvailability({ readOnly: false });
        retryingMessageIds.value = new Set();
        setReadSyncIdle();
        messagesFetchId.value++;
        clearBlobUrlCache();
    };

    const setSelectedConversationId = (
        id: number | null,
        kind: ConversationKind = ConversationKind.NATIVE
    ): void => {
        if (selectedConversationId.value === id && selectedConversationKind.value === kind) {
            return;
        }

        advanceConversationSelectionGeneration();
        resetSelectedConversationContext();
        resetMessagesFetch();
        selectedConversationId.value = id;
        selectedConversationKind.value = id === null ? null : kind;

        if (selectedConversation.value) {
            applySelectedConversationContext(selectedConversation.value);
        }
    };

    const formatMessage = (message: MessageItem): Message => {
        const userStore = useUserStore();
        const isLegacyConversationMessage =
            message.conversation_id === null && !!message.legacy_sms_conversation_id;
        const conversationKind =
            message.conversation_kind ??
            (isLegacyConversationMessage ? ConversationKind.LEGACY : ConversationKind.NATIVE);
        const conversationId = message.conversation_id ?? message.legacy_sms_conversation_id!;

        const isIncoming = message.communication_type_id === CommunicationType.INCOMING;
        const messageDatetime = new ActivixDate(message.original_created_at);
        const senderIsAuthUser = !isIncoming && userStore.authUser.isSameUser(message.user_id);
        const senderName = isIncoming ? '' : userStore.getUserNameById(message.user_id);

        return {
            id: message.id,
            conversationId,
            conversationKind,
            legacySmsConversationId: message.legacy_sms_conversation_id ?? null,
            communicationId: message.communication_id,
            text: message.message,
            isIncoming,
            senderIsAuthUser,
            senderName,
            createdAt: message.original_created_at,
            time: messageDatetime.toHumanTime(),
            attachments: message.media.map((mediaItem, index) => ({
                id: mediaItem.id,
                url: mediaItem.url,
                filename: mediaItem.file_name || `attachment-${index}`,
                mimeType: mediaItem.mime_type,
            })),
            appointmentId: message.appointment_id,
            appointment: message.appointment
                ? AppointmentStatus.fromApi(message.appointment)
                : null,
            status: message.status,
            errorCode: message.error_code,
        };
    };

    const ensureSelectedConversationMessageLoaded = async (
        conversationId: number,
        messageId: number
    ): Promise<void> => {
        if (!selectedConversationMatches(ConversationKind.NATIVE, conversationId)) {
            return;
        }

        const alreadyLoaded = selectedConversationMessages.value.some(
            (message) => message.id === messageId
        );

        if (alreadyLoaded) {
            return;
        }

        const response = await api.fetchConversationMessage(conversationId, messageId);

        // The selected conversation may have changed while the fetch was in flight.
        if (!selectedConversationMatches(ConversationKind.NATIVE, conversationId)) {
            return;
        }

        addMessage(formatMessage(response.data), true);
    };

    const syncCurrentUserReadState = (conversation: Conversation, conversationId: number): void => {
        if (!selectedConversationMatches(conversation.kind, conversationId)) {
            return;
        }

        if (conversation.currentUserId === null) {
            return;
        }

        const currentConversationUser = conversation.conversationUsers.find(
            (conversationUser) => conversationUser.userId === conversation.currentUserId
        );

        if (!currentConversationUser) {
            return;
        }

        const isStaleExternalBroadcast = isReadSyncPending();

        if (isStaleExternalBroadcast) {
            currentConversationUser.allMessagesReadAt = null;
            return;
        }

        if (currentConversationUser.allMessagesReadAt) {
            setReadSyncIdle();
        }
    };

    const updateConversationUsers = (
        conversationId: number,
        conversationUsers: ApiConversationUser[],
        kind: ConversationKind = ConversationKind.NATIVE
    ): void => {
        const conversation = getConversationById(conversationId, kind);

        if (!conversation) {
            return;
        }

        conversation.conversationUsers = conversationUsers.map(ConversationUser.fromApi);
        syncCurrentUserReadState(conversation, conversationId);
    };

    const updateConversationArchived = (conversationId: number, archived: boolean): void => {
        const conversation = getConversationById(conversationId);

        if (conversation) {
            conversation.archived = archived;
        }
    };

    const replaceConversation = (
        previousConversationId: number | null,
        conversation: Conversation
    ): void => {
        const index = findConversationIndex(
            conversation.id,
            conversation.kind,
            previousConversationId
        );

        if (index === -1) {
            conversations.value.unshift(conversation);
            return;
        }

        conversations.value.splice(index, 1, conversation);
    };

    const promoteResolvedConversation = (
        response: SendMessageResponse,
        previousConversation: Conversation,
        selectionGenerationAtRequestStart: number
    ): boolean => {
        const userStore = useUserStore();
        const currentUserId = userStore.authUser?.id ?? null;
        const resolvedConversation = Conversation.fromApi(
            response.conversation.data,
            {},
            currentUserId
        );
        const currentResolvedConversation = getConversationByIdentityKey(
            resolvedConversation.identityKey
        );

        if (
            currentResolvedConversation &&
            currentResolvedConversation.latestMessageId !== null &&
            (resolvedConversation.latestMessageId === null ||
                currentResolvedConversation.latestMessageId > resolvedConversation.latestMessageId)
        ) {
            resolvedConversation.latestMessageId = currentResolvedConversation.latestMessageId;
            resolvedConversation.lastMessage = currentResolvedConversation.lastMessage;
            resolvedConversation.time = currentResolvedConversation.time;
        }

        const selectedConversationWasPromotedFromDraft =
            previousConversation.id < 0 &&
            selectedConversationIdentityKey.value === resolvedConversation.identityKey;
        const selectionIsCurrent =
            conversationSelectionGeneration.value === selectionGenerationAtRequestStart &&
            (selectedConversationIdentityKey.value === previousConversation.identityKey ||
                selectedConversationWasPromotedFromDraft);

        if (previousConversation.id < 0) {
            const draftIndex = findConversationIndex(previousConversation.id);

            if (draftIndex !== -1) {
                conversations.value.splice(draftIndex, 1);
            }

            // A background send may resolve after the canonical conversation was loaded.
            // Keep that newer object and only update its message preview below.
            if (selectionIsCurrent || !currentResolvedConversation) {
                replaceConversation(null, resolvedConversation);
            }
        } else if (selectionIsCurrent) {
            replaceConversation(previousConversation.id, resolvedConversation);
        }

        if (selectionIsCurrent && previousConversation.id < 0) {
            selectedConversationId.value = resolvedConversation.id;
            selectedConversationKind.value = resolvedConversation.kind;
        }

        if (selectionIsCurrent) {
            const availability = {
                readOnly: response.readOnly ?? selectedConversationReadOnly.value,
                canSend: response.canSend,
                sendDisabledReason: response.sendDisabledReason,
            };

            if (selectedConversationCtaAvailability.value) {
                selectedConversationCtaAvailability.value = availability;
            }

            applyConversationAvailability(availability);
        }

        return selectionIsCurrent;
    };

    const retryMessage = async (messageId: number): Promise<void> => {
        const conversationId = selectedConversationId.value;

        if (
            !conversationId ||
            selectedConversationKind.value === ConversationKind.LEGACY ||
            selectedConversationReadOnly.value ||
            !selectedConversationCanSend.value ||
            retryingMessageIds.value.has(messageId)
        ) {
            return;
        }

        retryingMessageIds.value = new Set(retryingMessageIds.value).add(messageId);

        try {
            const response = await api.retryMessage(conversationId, messageId);
            const updatedMessage = formatMessage(response.data);

            const index = selectedConversationMessages.value.findIndex(
                (message) => message.id === messageId
            );

            if (index !== -1) {
                selectedConversationMessages.value.splice(index, 1, updatedMessage);
            }
        } finally {
            const nextRetryingMessageIds = new Set(retryingMessageIds.value);
            nextRetryingMessageIds.delete(messageId);
            retryingMessageIds.value = nextRetryingMessageIds;
        }
    };

    const toggleArchived = async (conversationId: number): Promise<boolean> => {
        const conversation = getConversationById(conversationId);

        if (
            selectedConversationKind.value === ConversationKind.LEGACY ||
            selectedConversationReadOnly.value ||
            conversation?.isLegacy
        ) {
            return false;
        }

        const response = await api.toggleArchived(conversationId);

        if (conversation) {
            conversation.archived = response.data.archived;
        }

        return response.data.archived;
    };

    const selectAndHydrateConversation = (
        conversation: Conversation,
        options: {
            readOnly: boolean;
            lastMessageReadIdAtLoad: number | null;
            canSend?: boolean;
            sendDisabledReason?: SendDisabledReason;
            contextLead?: ConversationLead | null;
            contextLeadPhone?: ConversationLeadPhone | null;
            sendTarget?: SmsConversationSendTarget | null;
            sourceCommunicationId?: number | null;
            exactTargetAvailability?: boolean;
        }
    ): void => {
        addConversation(conversation);
        selectedConversationId.value = conversation.id;
        selectedConversationKind.value = conversation.kind;
        selectedConversationCtaAvailability.value = options.exactTargetAvailability
            ? {
                  readOnly: options.readOnly,
                  canSend: options.canSend,
                  sendDisabledReason: options.sendDisabledReason,
              }
            : null;
        applyConversationAvailability(options);
        selectedConversationLastMessageReadIdAtLoad.value = options.lastMessageReadIdAtLoad;
        applySelectedConversationContext(conversation, options);
    };

    const findOrCreateConversation = async (
        lead: ConversationLead,
        phone: ConversationLeadPhone,
        target:
            | { leadPhoneId: number; contactPhoneId?: never }
            | { contactPhoneId: number; leadPhoneId?: never }
    ): Promise<void> => {
        if (
            isConversationLoaded(lead.id, target) &&
            !selectedConversationReadOnly.value &&
            selectedConversationCanSend.value
        ) {
            conversationLookupGeneration++;
            return;
        }

        const lookupGeneration = ++conversationLookupGeneration;

        let result: Awaited<ReturnType<typeof api.findConversationByLeadPhone>>;

        try {
            result = await api.findConversationByLeadPhone({
                leadId: lead.id,
                ...target,
                perPage: 25,
            });
        } catch (error) {
            if (conversationLookupGeneration !== lookupGeneration) {
                return;
            }

            throw error;
        }

        if (conversationLookupGeneration !== lookupGeneration) {
            return;
        }

        if (!result || isConversationNotFoundResponse(result)) {
            const smsProvider = result?.smsProvider
                ? ConversationSmsProvider.fromApi(result.smsProvider)
                : null;

            if (!smsProvider) {
                throw new Error('No SMS provider available');
            }

            advanceConversationSelectionGeneration();
            resetSelectedConversationContext();
            resetMessagesFetch();
            addDraftConversation(lead, phone, smsProvider, result?.contactTarget);
            return;
        }

        const conversation = Conversation.fromApi(
            result.conversation.data,
            {},
            resolveCurrentUserIdForAccount(result.conversation.data.lead.account_id)
        );
        const contextLead = buildContextLead(result.contextLead, lead);
        const contextLeadPhone = result.contextLeadPhone
            ? ConversationLeadPhone.fromApi(result.contextLeadPhone)
            : phone;
        const sendTarget = result.contactTarget
            ? buildContactSendTarget(result.contactTarget, contextLead)
            : {
                  lead: contextLead,
                  leadPhoneId: contextLeadPhone.id,
                  contactPhoneId: null,
              };

        advanceConversationSelectionGeneration();
        resetSelectedConversationContext();
        resetMessagesFetch();
        selectAndHydrateConversation(conversation, {
            readOnly: result.readOnly,
            canSend: result.canSend,
            sendDisabledReason: result.sendDisabledReason,
            lastMessageReadIdAtLoad: result.lastMessageReadId,
            contextLead,
            contextLeadPhone,
            sendTarget,
            exactTargetAvailability: true,
        });
        messagesNextPageCursor.value = result.meta.next_cursor;
        addOrderedMessagesFromApi(result.data);
    };

    const findOrCreateConversationByLead = async (
        lead: ConversationLead,
        phone: ConversationLeadPhone
    ): Promise<void> => findOrCreateConversation(lead, phone, { leadPhoneId: phone.id });

    const findOrCreateConversationByContact = async (
        lead: ConversationLead,
        contactTarget: ApiContactTarget
    ): Promise<void> => {
        const phone = new ConversationLeadPhone({
            id: contactTarget.lead_phone_id ?? 0,
            phone: contactTarget.destination,
            type: 'mobile',
            preferred: true,
        });

        return findOrCreateConversation(lead, phone, {
            contactPhoneId: contactTarget.contact_phone_id,
        });
    };

    const openConversationFromCommunication = async (
        lead: ConversationLead,
        communicationId: number
    ): Promise<void> => {
        const selectionGeneration = advanceConversationSelectionGeneration();
        const lookupGeneration = conversationLookupGeneration;
        resetSelectedConversationContext();
        resetMessagesFetch();

        let response: PaginatedMessageListWithConversation;

        try {
            response = await api.fetchConversationByCommunicationId(communicationId, {
                perPage: 25,
            });
        } catch (error) {
            if (
                conversationSelectionGeneration.value !== selectionGeneration ||
                conversationLookupGeneration !== lookupGeneration
            ) {
                return;
            }

            throw error;
        }

        if (
            conversationSelectionGeneration.value !== selectionGeneration ||
            conversationLookupGeneration !== lookupGeneration
        ) {
            return;
        }

        const conversation = Conversation.fromApi(
            response.conversation.data,
            {},
            resolveCurrentUserIdForAccount(response.conversation.data.lead.account_id)
        );
        const contextLead = buildContextLead(response.contextLead, lead);

        selectAndHydrateConversation(conversation, {
            readOnly: response.readOnly,
            canSend: response.canSend,
            sendDisabledReason: response.sendDisabledReason,
            lastMessageReadIdAtLoad: response.lastMessageReadId,
            contextLead,
            contextLeadPhone: response.contextLeadPhone
                ? ConversationLeadPhone.fromApi(response.contextLeadPhone)
                : null,
            sendTarget: response.readOnly ? null : buildDefaultSendTarget(conversation),
            sourceCommunicationId: communicationId,
        });
        addOrderedMessagesFromApi(response.data);

        if (!response.readOnly && response.conversation.data.kind === ConversationKind.NATIVE) {
            setReadSyncPending();
        }

        if (messageExistsForCommunication(communicationId)) {
            deferredMessagesNextPageCursor.value = response.meta.next_cursor;
        } else {
            messagesNextPageCursor.value = response.meta.next_cursor;
            await seekMessageInHistory(() => messageExistsForCommunication(communicationId));
        }
    };

    const activateDeferredCursor = (): void => {
        if (!deferredMessagesNextPageCursor.value) {
            return;
        }

        messagesNextPageCursor.value = deferredMessagesNextPageCursor.value;
        deferredMessagesNextPageCursor.value = null;
        messagesFetchId.value++;
    };

    const seekMessageInHistory = async (
        predicate: (messages: Message[]) => boolean,
        options?: { maxPages?: number }
    ): Promise<boolean> => {
        const maxPages = options?.maxPages ?? Infinity;
        let pagesFetched = 0;

        while (pagesFetched < maxPages) {
            if (predicate(selectedConversationMessages.value)) {
                return true;
            }

            if (!messagesNextPageCursor.value) {
                return false;
            }

            // eslint-disable-next-line no-await-in-loop -- Sequential page fetching is intentional
            const status = await fetchConversationMessages();

            if (status !== FetchStatus.SUCCESS) {
                return predicate(selectedConversationMessages.value);
            }

            pagesFetched++;
        }

        return predicate(selectedConversationMessages.value);
    };

    const messageExistsForCommunication = (communicationId: number): boolean => {
        return selectedConversationMessages.value.some(
            (item) => item.communicationId === communicationId
        );
    };

    const isConversationLoaded = (
        leadId: number,
        target:
            | { leadPhoneId: number; contactPhoneId?: never }
            | { contactPhoneId: number; leadPhoneId?: never }
    ): boolean => {
        const sendTarget = selectedConversationSendTarget.value;

        return (
            sendTarget?.lead.id === leadId &&
            ('contactPhoneId' in target
                ? sendTarget.contactPhoneId === target.contactPhoneId
                : sendTarget.leadPhoneId === target.leadPhoneId)
        );
    };

    const isConversationNotFoundResponse = (
        result: PaginatedMessageListWithConversation | ConversationNotFoundResponse
    ): result is ConversationNotFoundResponse => {
        return (result as ConversationNotFoundResponse).error?.code === 'conversation_not_found';
    };

    return {
        // State
        conversations: computed(() => conversations.value),
        conversationsFetchId: computed(() => conversationsFetchId.value),

        selectedConversationMessages: computed(() => selectedConversationMessages.value),
        selectedConversationLastMessageReadIdAtLoad: computed(
            () => selectedConversationLastMessageReadIdAtLoad.value
        ),
        selectedConversationReadOnly: computed(() => selectedConversationReadOnly.value),
        selectedConversationCanSend: computed(() => selectedConversationCanSend.value),
        selectedConversationSendDisabledReason: computed(
            () => selectedConversationSendDisabledReason.value
        ),
        selectedConversationContextLead: computed(() => selectedConversationContextLead.value),
        selectedConversationContextLeadPhone: computed(
            () => selectedConversationContextLeadPhone.value
        ),
        selectedConversationSendTarget: computed(() => selectedConversationSendTarget.value),
        selectedConversationDisplayLead,
        selectedConversationDisplayPhone,
        messagesFetchId: computed(() => messagesFetchId.value),

        selectedConversation: computed(() => selectedConversation.value),
        selectedConversationId: computed(() => selectedConversationId.value),
        selectedConversationKind: computed(() => selectedConversationKind.value),
        selectedConversationIdentityKey,
        conversationSelectionGeneration: computed(() => conversationSelectionGeneration.value),

        // Getters
        conversationCount,
        currentLead,
        currentLeadId,
        hasFetchedAllConversations,
        hasFetchedAllMessages,

        // Actions
        fetchConversations,
        fetchConversationMessages,
        setSelectedConversationId,
        addConversation,
        parseTemplate,
        prependConversation,
        bumpConversationsFetchId,
        resetConversationsFetch,
        resetMessagesFetch,
        sendMessage,
        retryMessage,
        toggleArchived,
        updateConversationArchived,
        handleNewSmsMessageBroadcast,
        handleAppointmentStatusChanged,
        syncSelectedConversationReadState,
        updateConversationUsers,
        addMessage,
        updateMessageStatus,
        formatMessage,
        getConversationById,
        buildDraftConversation,
        addDraftConversation,
        findOrCreateConversationByLead,
        findOrCreateConversationByContact,
        openConversationFromCommunication,
        activateDeferredCursor,
        seekMessageInHistory,
    };
});
